import React, { useState, useEffect, useImperativeHandle, forwardRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRotate, faSpinner, faEdit, faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import { orderApi } from '../api/orderApi';
import { marketDataApi } from '../api/marketDataApi';
import { schwabApi } from '../api/schwabApi';

const ALPACA_OPEN = new Set(['new', 'accepted', 'pending_new', 'pending_replace', 'pending_cancel', 'partially_filled', 'held']);
const SCHWAB_OPEN = new Set([
  'WORKING',
  'QUEUED',
  'PENDING_ACTIVATION',
  'ACCEPTED',
  'AWAITING_CONDITION',
  'NEW',
  'PENDING_CANCEL',
  'PENDING_REPLACE',
]);

function normalizeAlpacaOrder(order) {
  return {
    ...order,
    id: order.id,
    venue: 'alpaca',
    side: order.side,
    type: order.type,
    qty: order.qty ?? order.quantity,
    limit_price: order.limit_price ?? order.limitPrice ?? null,
    filled_qty: order.filled_qty ?? order.filledQty ?? order.filled_quantity ?? 0,
    status: order.status,
  };
}

function normalizeSchwabOrder(order) {
  const instr = String(order.instruction || '').toUpperCase();
  let side = 'buy';
  if (instr.includes('SELL')) side = 'sell';
  else if (instr.includes('BUY')) side = 'buy';
  return {
    id: `schwab-${order.orderId}`,
    orderId: order.orderId,
    venue: 'schwab',
    accountNumber: order.accountNumber,
    symbol: order.symbol,
    side,
    type: String(order.orderType || 'MARKET').toLowerCase(),
    qty: order.quantity ?? order.filledQuantity ?? 0,
    quantity: order.quantity,
    limit_price: order.price != null ? Number(order.price) : null,
    filled_qty: Number(order.filledQuantity || 0),
    status: order.status,
    cancelable: Boolean(order.cancelable),
  };
}

const OpenOrdersTable = forwardRef(({ onOrderUpdated }, ref) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cancelingOrderId, setCancelingOrderId] = useState(null);
  const [editingOrderId, setEditingOrderId] = useState(null);
  const [editPrice, setEditPrice] = useState('');
  const [editQty, setEditQty] = useState('');
  const [updatingOrderId, setUpdatingOrderId] = useState(null);
  const [quotes, setQuotes] = useState({});
  const [loadingQuotes, setLoadingQuotes] = useState(false);

  const fetchQuotes = async (symbols) => {
    if (symbols.length === 0) return {};

    setLoadingQuotes(true);
    const quotesMap = {};

    const quotePromises = symbols.map(async (symbol) => {
      try {
        const response = await marketDataApi.getQuote(symbol);
        const quote = response.data || response;
        let bidPrice = quote?.bidPrice ?? quote?.bp ?? null;
        let askPrice = quote?.askPrice ?? quote?.ap ?? null;
        const midPrice =
          bidPrice != null && askPrice != null ? (Number(bidPrice) + Number(askPrice)) / 2 : null;
        quotesMap[symbol] = {
          bidPrice,
          askPrice,
          midPrice: midPrice || askPrice || bidPrice || null,
        };
      } catch (err) {
        console.error(`Failed to fetch quote for ${symbol}:`, err);
        quotesMap[symbol] = { bidPrice: null, askPrice: null, midPrice: null };
      }
    });

    await Promise.all(quotePromises);
    setQuotes(quotesMap);
    setLoadingQuotes(false);
  };

  const loadAlpacaOpen = async () => {
    try {
      const response = await orderApi.getOrders({ status: 'open' });
      return (response.data || [])
        .filter((order) => ALPACA_OPEN.has(String(order.status || '').toLowerCase()))
        .map(normalizeAlpacaOrder);
    } catch (err) {
      if (err.response?.status === 400 || err.message?.includes('status')) {
        const response = await orderApi.getOrders({});
        return (response.data || [])
          .filter((order) => ALPACA_OPEN.has(String(order.status || '').toLowerCase()))
          .map(normalizeAlpacaOrder);
      }
      throw err;
    }
  };

  const loadSchwabOpen = async () => {
    try {
      const statusRes = await schwabApi.getStatus();
      const connected = Boolean(statusRes?.connected || statusRes?.data?.connected);
      if (!connected) return [];

      // Prefer WORKING filter; fall back to recent orders and client-filter
      let list = [];
      try {
        const res = await schwabApi.getOrders({ days: 7, status: 'WORKING' });
        list = res?.data || [];
      } catch {
        const res = await schwabApi.getOrders({ days: 7 });
        list = res?.data || [];
      }
      return (Array.isArray(list) ? list : [])
        .filter((o) => SCHWAB_OPEN.has(String(o.status || '').toUpperCase()))
        .map(normalizeSchwabOrder);
    } catch (err) {
      // Not connected / auth — skip quietly; Alpaca orders still show
      console.warn('Schwab open orders unavailable:', err.response?.data?.error || err.message);
      return [];
    }
  };

  const loadOpenOrders = useCallback(async (showLoading = true) => {
    try {
      if (showLoading && orders.length === 0) setLoading(true);

      const [alpacaResult, schwabResult] = await Promise.allSettled([
        loadAlpacaOpen(),
        loadSchwabOpen(),
      ]);

      const alpaca = alpacaResult.status === 'fulfilled' ? alpacaResult.value : [];
      const schwab = schwabResult.status === 'fulfilled' ? schwabResult.value : [];

      if (alpacaResult.status === 'rejected' && schwab.length === 0) {
        throw alpacaResult.reason;
      }

      const merged = [...alpaca, ...schwab].sort((a, b) => {
        const ta = new Date(a.submittedAt || a.enteredTime || 0).getTime();
        const tb = new Date(b.submittedAt || b.enteredTime || 0).getTime();
        return tb - ta;
      });

      setOrders(merged);
      setError(
        alpacaResult.status === 'rejected'
          ? alpacaResult.reason?.response?.data?.error || alpacaResult.reason?.message || null
          : null
      );

      const uniqueSymbols = [...new Set(merged.map((order) => order.symbol).filter(Boolean))];
      if (uniqueSymbols.length > 0) {
        await fetchQuotes(uniqueSymbols);
      } else {
        setQuotes({});
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      if (showLoading) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useImperativeHandle(ref, () => ({
    refresh: () => loadOpenOrders(false),
    loadOpenOrders: () => loadOpenOrders(false),
  }));

  useEffect(() => {
    loadOpenOrders();
  }, [loadOpenOrders]);

  const handleCancelOrder = async (order) => {
    if (!window.confirm(`Cancel ${order.venue === 'schwab' ? 'Schwab' : 'Alpaca'} order for ${order.symbol}?`)) {
      return;
    }

    try {
      setCancelingOrderId(order.id);
      if (order.venue === 'schwab') {
        await schwabApi.cancelOrder(order.orderId, order.accountNumber);
      } else {
        await orderApi.cancelOrder(order.id);
      }
      await loadOpenOrders(false);
      if (onOrderUpdated) onOrderUpdated();
    } catch (err) {
      alert(`Failed to cancel order: ${err.response?.data?.error || err.message}`);
    } finally {
      setCancelingOrderId(null);
    }
  };

  const handleEditOrder = (order) => {
    if (order.venue === 'schwab') return;
    setEditingOrderId(order.id);
    setEditPrice(order.limit_price?.toString() || '');
    setEditQty((order.qty || order.quantity || 0).toString());
  };

  const handleCancelEdit = () => {
    setEditingOrderId(null);
    setEditPrice('');
    setEditQty('');
  };

  const handleUpdateOrder = async (orderId) => {
    const order = orders.find((o) => o.id === orderId);
    if (!order || order.venue === 'schwab') return;

    const newQty = parseFloat(editQty);
    if (isNaN(newQty) || newQty <= 0) {
      alert('Please enter a valid quantity greater than 0');
      return;
    }

    const isLimitOrder =
      order.type?.toLowerCase() === 'limit' || order.type?.toLowerCase() === 'stop_limit';
    if (isLimitOrder) {
      const newPrice = parseFloat(editPrice);
      if (isNaN(newPrice) || newPrice <= 0) {
        alert('Please enter a valid price greater than 0');
        return;
      }
    }

    try {
      setUpdatingOrderId(orderId);
      const updateParams = { qty: newQty };
      if (isLimitOrder) {
        updateParams.limitPrice = parseFloat(editPrice);
      }
      await orderApi.replaceOrder(orderId, updateParams);
      setEditingOrderId(null);
      setEditPrice('');
      setEditQty('');
      await loadOpenOrders(false);
      if (onOrderUpdated) onOrderUpdated();
    } catch (err) {
      alert(`Failed to update order: ${err.response?.data?.error || err.message}`);
    } finally {
      setUpdatingOrderId(null);
    }
  };

  const formatCurrency = (value) => {
    if (value == null || value === '') return 'N/A';
    const n = Number(value);
    if (!Number.isFinite(n)) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(n);
  };

  if (!loading && orders.length === 0 && !error) {
    return null;
  }

  if (loading && orders.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="animate-pulse">
          <div className="h-5 bg-gray-200 rounded w-1/4 mb-3"></div>
          <div className="h-48 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (error && orders.length === 0) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-3">
        <p className="text-red-800 text-xs">Error: {error}</p>
      </div>
    );
  }

  const alpacaCount = orders.filter((o) => o.venue !== 'schwab').length;
  const schwabCount = orders.filter((o) => o.venue === 'schwab').length;

  return (
    <div className="bg-white rounded-lg shadow-md p-4 border border-gray-100">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div>
          <h2 className="text-lg font-bold text-gray-800">Open Orders ({orders.length})</h2>
          <div className="text-[10px] text-gray-500 mt-0.5">
            {alpacaCount > 0 && <span className="mr-2">Alpaca {alpacaCount}</span>}
            {schwabCount > 0 && <span>Schwab {schwabCount}</span>}
            {alpacaCount === 0 && schwabCount === 0 ? 'None' : null}
          </div>
        </div>
        <button
          onClick={() => loadOpenOrders(true)}
          disabled={loading}
          className="px-3 py-1 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium"
        >
          {loading ? (
            <>
              <FontAwesomeIcon icon={faSpinner} className="mr-1 animate-spin text-xs" />
              Refreshing...
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faRotate} className="mr-1 text-xs" />
              Refresh
            </>
          )}
        </button>
      </div>

      {orders.length === 0 ? (
        <div className="text-center py-8 text-gray-500 text-xs">No open orders</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-xs">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider">
                  Venue
                </th>
                <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider">
                  Symbol
                </th>
                <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider">
                  Side
                </th>
                <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider">
                  Qty
                </th>
                <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider">
                  Limit
                </th>
                <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider">
                  Bid
                </th>
                <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider">
                  Ask
                </th>
                <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider">
                  Filled
                </th>
                <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {orders.map((order) => {
                const isSchwab = order.venue === 'schwab';
                const canCancel = isSchwab ? order.cancelable !== false : true;
                return (
                  <tr key={order.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-2 py-1.5">
                      <span
                        className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide ${
                          isSchwab ? 'bg-blue-100 text-blue-800' : 'bg-violet-100 text-violet-800'
                        }`}
                      >
                        {isSchwab ? 'SCH' : 'ALP'}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 font-semibold text-indigo-600">{order.symbol || '—'}</td>
                    <td className="px-2 py-1.5">
                      <span
                        className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                          order.side?.toLowerCase() === 'buy' || order.side?.toLowerCase() === 'long'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {String(order.side || '').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 text-gray-700">{String(order.type || '').toUpperCase()}</td>
                    <td className="px-2 py-1.5 text-gray-700">
                      {editingOrderId === order.id ? (
                        <input
                          type="number"
                          step="0.0001"
                          value={editQty}
                          onChange={(e) => setEditQty(e.target.value)}
                          className="w-16 px-1.5 py-0.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500"
                          placeholder="Qty"
                          autoFocus
                        />
                      ) : (
                        parseFloat(order.qty || order.quantity || 0).toFixed(4)
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-gray-700">
                      {editingOrderId === order.id ? (
                        <div className="flex items-center gap-1">
                          {order.type?.toLowerCase() === 'limit' ||
                          order.type?.toLowerCase() === 'stop_limit' ? (
                            <input
                              type="number"
                              step="0.01"
                              value={editPrice}
                              onChange={(e) => setEditPrice(e.target.value)}
                              className="w-20 px-1.5 py-0.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500"
                              placeholder="Price"
                            />
                          ) : (
                            <span className="text-gray-400 text-[10px]">N/A</span>
                          )}
                          <button
                            onClick={() => handleUpdateOrder(order.id)}
                            disabled={updatingOrderId === order.id}
                            className="px-1.5 py-0.5 bg-green-600 text-white rounded hover:bg-green-700 text-[10px] font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Save"
                          >
                            {updatingOrderId === order.id ? (
                              <FontAwesomeIcon icon={faSpinner} className="animate-spin text-[10px]" />
                            ) : (
                              <FontAwesomeIcon icon={faCheck} className="text-[10px]" />
                            )}
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            disabled={updatingOrderId === order.id}
                            className="px-1.5 py-0.5 bg-gray-600 text-white rounded hover:bg-gray-700 text-[10px] font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Cancel"
                          >
                            <FontAwesomeIcon icon={faTimes} className="text-[10px]" />
                          </button>
                        </div>
                      ) : order.limit_price != null ? (
                        formatCurrency(order.limit_price)
                      ) : (
                        'N/A'
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-gray-700 font-medium">
                      {loadingQuotes ? (
                        <FontAwesomeIcon icon={faSpinner} className="animate-spin text-xs" />
                      ) : quotes[order.symbol]?.bidPrice != null ? (
                        <span className="text-green-600">{formatCurrency(quotes[order.symbol].bidPrice)}</span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-gray-700 font-medium">
                      {loadingQuotes ? (
                        <FontAwesomeIcon icon={faSpinner} className="animate-spin text-xs" />
                      ) : quotes[order.symbol]?.askPrice != null ? (
                        <span className="text-red-600">{formatCurrency(quotes[order.symbol].askPrice)}</span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-2 py-1.5">
                      <span
                        className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                          String(order.status || '').toLowerCase() === 'filled'
                            ? 'bg-green-100 text-green-800'
                            : String(order.status || '')
                                  .toLowerCase()
                                  .includes('cancel') ||
                                String(order.status || '').toLowerCase() === 'rejected'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        {String(order.status || '').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 text-gray-700">
                      {parseFloat(order.filled_qty || order.filled_quantity || 0).toFixed(4)}
                    </td>
                    <td className="px-2 py-1.5">
                      <div className="flex items-center gap-1">
                        {!isSchwab && editingOrderId !== order.id && (
                          <button
                            onClick={() => handleEditOrder(order)}
                            disabled={cancelingOrderId === order.id || updatingOrderId === order.id}
                            className="px-2 py-0.5 bg-blue-600 text-white rounded hover:bg-blue-700 text-[10px] font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-0.5"
                            title="Edit order"
                          >
                            <FontAwesomeIcon icon={faEdit} className="text-[10px]" />
                            Edit
                          </button>
                        )}
                        {editingOrderId !== order.id && canCancel && (
                          <button
                            onClick={() => handleCancelOrder(order)}
                            disabled={cancelingOrderId === order.id || updatingOrderId === order.id}
                            className="px-2 py-0.5 bg-red-600 text-white rounded hover:bg-red-700 text-[10px] font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {cancelingOrderId === order.id ? 'Canceling...' : 'Cancel'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
});

OpenOrdersTable.displayName = 'OpenOrdersTable';

export default OpenOrdersTable;
